import React, { useEffect, useState } from 'react';
import { Button, Card, Row, Col, Tag, Tabs, Table, Badge, Tooltip, Progress, Input, Select, message, Modal } from 'antd';
import { PageContentLayout } from '../../../shared/layout';
import {
  SettingOutlined,
  LeftOutlined,
  UserOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { withRouter, useParams, Link } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import OverlaySpinner from '../../../shared/OverlaySpinner';
import { startCase } from 'lodash';
import moment from 'moment';
import ProjectSettings from './ProjectSettings';
import AddExistingTaskModal from './AddExistingTaskModal';
import ProjectMilestone from './ProjectMilestone';
import ProjectGantt from './ProjectGantt';
import { urgencyColors } from '../configs/task.config';
import { projectStatusColors } from '../configs/project.config';
import AvatarS3 from '../../../shared/images/AvatarS3';
const { TabPane } = Tabs;
const { Option } = Select;

const ProjectDetails = ({ history }) => {
  const { id: currentProjectId } = useParams();
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [isAddTaskModalVisible, setIsAddTaskModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [searchText, setSearchText] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddToMilestoneModalVisible, setIsAddToMilestoneModalVisible] = useState(false);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
  const [isAddingToMilestone, setIsAddingToMilestone] = useState(false);

  const {
    currentUser,
    fetchProject,
    currentProject,
    projectLoading,
    refetchProject,
    updateProject,
    updateProjectLoading,
    fetchMilestones,
    milestones,
    updateMilestone,
  } = useProjectContext();
  const { updateTask } = useTaskContext();

  useEffect(() => {
    if (currentProjectId) {
      fetchProject({
        variables: { where: { id: currentProjectId } },
      });
      fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const handleStatusUpdate = async (selectedStatus) => {
    try {
      await updateProject({
        variables: {
          data: {
            status: selectedStatus,
          },
          where: { id: currentProjectId },
        },
      });
      await refetchProject();
      message.success('Project status updated successfully');
    } catch (error) {
      console.error('Failed to update project status:', error);
      message.error('Failed to update project status');
    }
  };

  const handleSettingsSave = async (values) => {
    try {
      const { team, owner, ...projectFields } = values;

      const updateData = { ...projectFields };

      if (team !== undefined) {
        const currentTeamIds = currentProject?.team?.map(member => member.id) || [];
        const newTeamIds = team || [];

        const usersToDisconnect = currentTeamIds.filter(id => !newTeamIds.includes(id));
        const usersToConnect = newTeamIds.filter(id => !currentTeamIds.includes(id));

        const teamUpdate = {};
        if (usersToDisconnect.length > 0) {
          teamUpdate.disconnect = usersToDisconnect.map(id => ({ id }));
        }
        if (usersToConnect.length > 0) {
          teamUpdate.connect = usersToConnect.map(id => ({ id }));
        }

        if (Object.keys(teamUpdate).length > 0) {
          updateData.team = teamUpdate;
        }
      }

      if (owner !== undefined) {
        const currentOwnerId = currentProject?.owner?.id;

        if (owner === null || owner === '') {
          if (currentOwnerId) {
            updateData.owner = { disconnect: true };
          }
        } else if (owner !== currentOwnerId) {
          updateData.owner = { connect: { id: owner } };
        }
      }

      await updateProject({
        variables: {
          data: updateData,
          where: { id: currentProjectId },
        },
      });

      await refetchProject();
      return Promise.resolve();
    } catch (error) {
      console.error('Update failed:', error);
      return Promise.reject(error);
    }
  };

  const handleRemoveTask = async (taskId, taskName) => {
    Modal.confirm({
      title: 'Remove Task from Project',
      content: `Are you sure you want to remove "${taskName}" from this project? This will also remove the task from any milestones in this project.`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
            },
          });

          const milestonesWithTask = milestones.filter(milestone =>
            milestone.tasks?.some(task => task.id === taskId)
          );

          for (const milestone of milestonesWithTask) {
            await updateMilestone({
              variables: {
                data: {
                  tasks: {
                    disconnect: [{ id: taskId }],
                  },
                },
                where: { id: milestone.id },
              },
            });
          }

          await updateProject({
            variables: {
              data: {
                tasks: {
                  disconnect: [{ id: taskId }],
                },
              },
              where: { id: currentProjectId },
            },
          });

          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
              orderBy: 'dueDate_ASC',
            },
          });

          await refetchProject();

          const milestoneMessage = milestonesWithTask.length > 0
            ? ` and removed from ${milestonesWithTask.length} milestone(s)`
            : '';
          message.success(`Task removed from project successfully${milestoneMessage}`);
        } catch (error) {
          console.error('Failed to remove task:', error);
          message.error('Failed to remove task from project');
        }
      },
    });
  };

  const handleBulkRemoveTasks = async () => {
    const taskCount = selectedTaskIds.length;
    const taskNames = currentProject?.tasks
      ?.filter(task => selectedTaskIds.includes(task.id))
      .map(task => task.name)
      .slice(0, 3);
    const displayNames = taskNames?.join(', ') + (taskCount > 3 ? `, and ${taskCount - 3} more` : '');

    Modal.confirm({
      title: `Remove ${taskCount} Task${taskCount > 1 ? 's' : ''} from Project`,
      content: `Are you sure you want to remove ${taskCount > 1 ? 'these tasks' : 'this task'} from this project? ${taskCount <= 3 ? `(${displayNames})` : `Including: ${displayNames}`}. This will also remove them from any milestones in this project.`,
      okText: 'Remove All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
            },
          });

          const affectedMilestones = milestones.filter(milestone =>
            milestone.tasks?.some(task => selectedTaskIds.includes(task.id))
          );

          for (const milestone of affectedMilestones) {
            const tasksToDisconnect = milestone.tasks
              .filter(task => selectedTaskIds.includes(task.id))
              .map(task => ({ id: task.id }));

            if (tasksToDisconnect.length > 0) {
              await updateMilestone({
                variables: {
                  data: {
                    tasks: {
                      disconnect: tasksToDisconnect,
                    },
                  },
                  where: { id: milestone.id },
                },
              });
            }
          }

          await updateProject({
            variables: {
              data: {
                tasks: {
                  disconnect: selectedTaskIds.map(id => ({ id })),
                },
              },
              where: { id: currentProjectId },
            },
          });

          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
              orderBy: 'dueDate_ASC',
            },
          });

          await refetchProject();
          setSelectedTaskIds([]);

          const milestoneMessage = affectedMilestones.length > 0
            ? ` and removed from ${affectedMilestones.length} milestone(s)`
            : '';
          message.success(`${taskCount} task${taskCount > 1 ? 's' : ''} removed from project successfully${milestoneMessage}`);
        } catch (error) {
          console.error('Failed to remove tasks:', error);
          message.error('Failed to remove tasks from project');
        }
      },
    });
  };

  const handleAddToMilestone = () => {
    setIsAddToMilestoneModalVisible(true);
  };

  const handleAddToMilestoneConfirm = async () => {
    if (!selectedMilestoneId || selectedTaskIds.length === 0) {
      message.warning('Please select a milestone');
      return;
    }

    setIsAddingToMilestone(true);

    try {
      const targetMilestone = milestones.find(m => m.id === selectedMilestoneId);
      const currentMilestoneTaskIds = targetMilestone?.tasks?.map(t => t.id) || [];
      const tasksToConnect = selectedTaskIds.filter(id => !currentMilestoneTaskIds.includes(id));

      if (tasksToConnect.length === 0) {
        message.info('Selected tasks are already in this milestone');
        setIsAddToMilestoneModalVisible(false);
        setIsAddingToMilestone(false);
        return;
      }

      // Find tasks that are in other milestones and need to be removed first
      const tasksToReassign = [];
      for (const taskId of tasksToConnect) {
        const existingMilestone = milestones.find(m =>
          m.id !== selectedMilestoneId && m.tasks?.some(t => t.id === taskId)
        );
        if (existingMilestone) {
          tasksToReassign.push({
            taskId,
            fromMilestone: existingMilestone,
          });
        }
      }

      // Remove tasks from their current milestones first
      for (const { taskId, fromMilestone } of tasksToReassign) {
        await updateMilestone({
          variables: {
            data: {
              tasks: {
                disconnect: [{ id: taskId }],
              },
            },
            where: { id: fromMilestone.id },
          },
        });
      }

      // Add tasks to the selected milestone
      await updateMilestone({
        variables: {
          data: {
            tasks: {
              connect: tasksToConnect.map(id => ({ id })),
            },
          },
          where: { id: selectedMilestoneId },
        },
      });

      await fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });

      await refetchProject();

      let successMessage = `Successfully added ${tasksToConnect.length} task(s) to milestone`;
      if (tasksToReassign.length > 0) {
        successMessage += ` (${tasksToReassign.length} reassigned from other milestones)`;
      }
      message.success(successMessage);
      setIsAddToMilestoneModalVisible(false);
      setSelectedMilestoneId(null);
      setSelectedTaskIds([]);
    } catch (error) {
      console.error('Failed to add tasks to milestone:', error);
      message.error('Failed to add tasks to milestone');
    } finally {
      setIsAddingToMilestone(false);
    }
  };

  if (!currentProjectId) return <p>Select a project to see the details.</p>;

  if (projectLoading) {
    return <OverlaySpinner />;
  }

  const hasAccess = () => {
    if (!currentProject || !currentUser) return false;

    // Channel partners don't have access to project management
    const userOrgLevel = currentUser.role?.org?.level;
    if (userOrgLevel === 'CHANNEL_PARTNER') return false;

    const currentUserOrgId = currentUser.role?.org?.id;
    if (!currentUserOrgId) return false;

    // Check if project owner has any role in an org that matches the current user's access
    // This matches the logic from ProjectBoard filtering
    const ownerRoles = currentProject.owner?.roles || [];

    return ownerRoles.some(role => {
      const roleOrgId = role.org?.id;
      const roleOrgParentId = role.org?.parentOrg?.id;

      // Same org
      if (roleOrgId === currentUserOrgId) return true;

      // Current user's org is parent of this role's org
      if (roleOrgParentId === currentUserOrgId) return true;

      return false;
    });
  };

  if (currentProject && !hasAccess()) {
    return (
      <div style={{ backgroundColor: 'white', paddingInline: '5%', height: '100vh', minHeight: '100vh' }}>
        <Row
          style={{
            paddingTop: '10px',
            paddingBottom: '20px',
          }}
        >
          <Button
            style={{ border: 'none', boxShadow: 'none' }}
            onClick={() => {
              history.goBack();
            }}
          >
            {<LeftOutlined style={{ paddingRight: '10px' }} />}{' '}
            <Text strong> Back </Text>
          </Button>
        </Row>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 80px)' }}>
          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444', display: 'block', marginBottom: 16 }}>
              Access Denied
            </Text>
            <Text style={{ fontSize: '16px', color: '#64748b', display: 'block', marginBottom: 24 }}>
              You do not have permission to view this project.
            </Text>
            <Text style={{ fontSize: '14px', color: '#94a3b8', display: 'block' }}>
              Only users in the same organization can access this project.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  const getProjectStats = () => {
    if (!currentProject || !currentProject.tasks) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0,
        daysLeft: null,
      };
    }

    const totalTasks = currentProject.tasks.length;
    const completedTasks = currentProject.tasks.filter(
      (task) => task.status === 'COMPLETED' || task.status === 'DONE'
    ).length;
    const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    let daysLeft = null;
    if (currentProject.endDate) {
      const today = moment();
      const endDate = moment(currentProject.endDate);
      daysLeft = endDate.diff(today, 'days');
    }

    return {
      totalTasks,
      completedTasks,
      completionPercentage,
      daysLeft,
    };
  };

  const stats = getProjectStats();

  const TasksTable = ({ tasks }) => {
    const columns = [
      {
        title: <span style={{ color: '#64748b' }}>Task Name</span>,
        dataIndex: 'name',
        key: 'name',
        width: '40%',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title={record.urgency ? `URGENCY: ${record.urgency}` : undefined}>
              <Badge
                dot
                color={urgencyColors[record.urgency] || 'white'}
                className="custom-badge"
                style={{
                  marginRight: 0,
                  alignSelf: 'center',
                }}
              />
            </Tooltip>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Link
                to={`/tasks/details/${record.id}`}
                style={{
                  textDecoration: 'none',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              >
                {text}
              </Link>
              {record.requestType && (
                <Text style={{ fontSize: '12px', color: '#94a3b8', marginTop: 2 }}>
                  {record.requestType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                </Text>
              )}
            </div>
          </div>
        ),
      },
      {
        title: <span style={{ color: '#64748b' }}>Due Date</span>,
        dataIndex: 'dueDate',
        key: 'dueDate',
        render: (dueDate) => {
          if (!dueDate) return '-';

          const today = moment();
          const due = moment(dueDate);
          const daysLeft = due.diff(today, 'days');

          let color = '#64748b';
          if (daysLeft < 0) {
            color = '#ff4d4f';
          } else if (daysLeft <= 3) {
            color = '#faad14';
          }

          return (
            <Text style={{ color }}>
              {moment(dueDate).format('MMM D, YYYY')}
            </Text>
          );
        },
      },
      {
        title: <span style={{ color: '#64748b' }}>Assignee</span>,
        dataIndex: 'assigneeUsers',
        key: 'assigneeUsers',
        render: (assigneeUsers) => {
          if (!assigneeUsers || assigneeUsers.length === 0) return '-';

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {assigneeUsers.slice(0, 3).map((user, idx) => (
                <Tooltip
                  key={user.id || idx}
                  title={startCase(user.name || user.username)}
                >
                  <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <AvatarS3
                      imgKey={user.avatar && user.avatar.key}
                      username={user.name || user.username}
                      avatarProps={{ size: 27 }}
                    />
                  </div>
                </Tooltip>
              ))}
              {assigneeUsers.length > 3 && (
                <Text style={{ fontSize: '12px', color: '#64748b' }}>
                  +{assigneeUsers.length - 3}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        title: <span style={{ color: '#64748b' }}>Channel Partner</span>,
        dataIndex: 'channelPartner',
        key: 'channelPartner',
        render: (channelPartner) => channelPartner?.name || '-',
      },
      {
        title: <span style={{ color: '#64748b' }}>Advertiser</span>,
        dataIndex: 'advertiser',
        key: 'advertiser',
        render: (advertiser) => advertiser?.name || '-',
      },
      {
        title: <span style={{ color: '#64748b' }}>Milestone</span>,
        key: 'milestone',
        render: (_, record) => {
          const milestone = (milestones || []).find(m =>
            m.tasks?.some(t => t.id === record.id)
          );
          return milestone ? (
            <Tag color="blue" style={{ margin: 0 }}>
              {milestone.name}
            </Tag>
          ) : (
            <span style={{ color: '#94a3b8' }}>-</span>
          );
        },
      },
      {
        title: <span style={{ color: '#64748b' }}>Status</span>,
        dataIndex: 'status',
        key: 'status',
        render: (status) => {
          if (!status) return 'N/A';
          const formattedStatus = status
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          return <Tag color={projectStatusColors[status] || 'default'}>{formattedStatus}</Tag>;
        },
      },
      {
        title: <span style={{ color: '#64748b' }}>Actions</span>,
        key: 'actions',
        width: '100px',
        align: 'center',
        render: (_, record) => (
          <Tooltip title="Remove task from project">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTask(record.id, record.name);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </Tooltip>
        ),
      },
    ];

    const allTaskIds = (currentProject?.tasks || []).map(task => task.id);
    const filteredTaskIds = tasks.map(task => task.id);

    const rowSelection = {
      selectedRowKeys: selectedTaskIds,
      preserveSelectedRowKeys: true,
      onChange: (selectedRowKeys) => {
        setSelectedTaskIds(selectedRowKeys);
      },
      selections: [
        {
          key: 'select-all-pages',
          text: 'Select All Tasks',
          onSelect: () => {
            setSelectedTaskIds(
              searchText
                ? (currentProject?.tasks || [])
                    .filter(task => task.name.toLowerCase().includes(searchText.toLowerCase()))
                    .map(task => task.id)
                : allTaskIds
            );
          },
        },
        {
          key: 'select-current-page',
          text: 'Select Current Page',
          onSelect: () => {
            const currentPageTaskIds = filteredTaskIds;
            setSelectedTaskIds(prev => {
              const newSelection = [...new Set([...prev, ...currentPageTaskIds])];
              return newSelection;
            });
          },
        },
        Table.SELECTION_INVERT,
        {
          key: 'clear-all',
          text: 'Clear All',
          onSelect: () => {
            setSelectedTaskIds([]);
          },
        },
      ],
    };

    return (
      <Table
        columns={columns}
        dataSource={tasks}
        rowKey="id"
        rowSelection={rowSelection}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, newPageSize) => {
            setCurrentPage(page);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }
          },
        }}
        locale={{ emptyText: 'No tasks available' }}
        className="custom-table"
      />
    );
  };

  return (
    <div style={{ backgroundColor: 'white', paddingInline: '5%', height: '100%', minHeight: '100vh' }}>
      <Row
        style={{
          paddingTop: '10px',
          paddingBottom: '5px',
        }}
      >
        <Button
          style={{ border: 'none', boxShadow: 'none' }}
          onClick={() => {
            history.goBack();
          }}
        >
          {<LeftOutlined style={{ paddingRight: '10px' }} />}{' '}
          <Text strong> Back </Text>
        </Button>
      </Row>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Text style={{ fontSize: '25px', fontWeight: 'bolder' }}>
            {currentProject && currentProject.name}
          </Text>
          {currentProject && currentProject.owner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserOutlined style={{ fontSize: '14px', color: '#64748b' }} />
              <Text style={{ fontSize: '14px', color: '#64748b' }}>
                {startCase(currentProject.owner.name || currentProject.owner.username)}
              </Text>
            </div>
          )}
        </div>
        <Col style={{ display: 'inline-flex', marginTop: '2vh', gap: '8px' }}>
          <Col style={{ paddingInline: '5px' }}>
            <Select
              className="custom-select"
              style={{ width: '180px', height: '41px' }}
              value={currentProject && currentProject.status}
              placeholder="Update Status"
              onChange={handleStatusUpdate}
              loading={updateProjectLoading || projectLoading}
              disabled={updateProjectLoading || projectLoading}
            >
              <Option value="NOT_STARTED">Not Started</Option>
              <Option value="PLANNING">Planning</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="COMPLETED">Completed</Option>
            </Select>
          </Col>
          <Col style={{ paddingInline: '5px' }}>
            <Button
              onClick={() => setIsSettingsModalVisible(true)}
              style={{
                fontSize: '15px',
                height: '41px',
                borderRadius: '5px',
                color: 'black',
                width: "100%",
              }}
            >
              {<SettingOutlined />} Settings
            </Button>
          </Col>
        </Col>
      </div>

      <ProjectSettings
        visible={isSettingsModalVisible}
        onCancel={() => setIsSettingsModalVisible(false)}
        project={currentProject}
        currentUser={currentUser}
        onSave={handleSettingsSave}
      />

      <AddExistingTaskModal
        visible={isAddTaskModalVisible}
        onCancel={() => setIsAddTaskModalVisible(false)}
        currentUser={currentUser}
        currentProject={currentProject}
        updateProject={updateProject}
        refetchProject={refetchProject}
        updateProjectLoading={updateProjectLoading}
        onAddTasks={async (taskIds, selectedTasks, selectedMilestoneIds = []) => {
          try {
            const currentTaskIds = currentProject?.tasks?.map(task => task.id) || [];
            const tasksToConnect = taskIds.filter(id => !currentTaskIds.includes(id));

            if (tasksToConnect.length > 0) {
              const projectMemberIds = currentProject?.team?.map(member => member.id) || [];
              const projectOwnerId = currentProject?.owner?.id;
              const allProjectUserIds = [...projectMemberIds, projectOwnerId].filter(Boolean);

              let convertedCount = 0;

              for (const task of selectedTasks) {
                if (task.assigneeUsers && task.assigneeUsers.length > 0) {
                  const assigneeIds = task.assigneeUsers.map(u => u.id);
                  const nonMemberAssigneeIds = assigneeIds.filter(id => !allProjectUserIds.includes(id));

                  if (nonMemberAssigneeIds.length > 0) {
                    const currentCollaboratorIds = task.collaborators?.map(c => c.id) || [];
                    const usersToAddAsCollaborators = nonMemberAssigneeIds.filter(id => !currentCollaboratorIds.includes(id));
                    const updateData = {};

                    if (nonMemberAssigneeIds.length > 0) {
                      updateData.assigneeUsers = {
                        disconnect: nonMemberAssigneeIds.map(id => ({ id }))
                      };
                    }

                    if (usersToAddAsCollaborators.length > 0) {
                      updateData.collaborators = {
                        connect: usersToAddAsCollaborators.map(id => ({ id }))
                      };
                    }

                    await updateTask({
                      variables: {
                        data: updateData,
                        where: { id: task.id },
                      },
                    });

                    convertedCount += nonMemberAssigneeIds.length;
                  }
                }
              }

              await updateProject({
                variables: {
                  data: {
                    tasks: {
                      connect: tasksToConnect.map(id => ({ id })),
                    },
                  },
                  where: { id: currentProjectId },
                },
              });

              if (selectedMilestoneIds.length > 0) {
                for (const milestoneId of selectedMilestoneIds) {
                  await updateMilestone({
                    variables: {
                      data: {
                        tasks: {
                          connect: tasksToConnect.map(id => ({ id })),
                        },
                      },
                      where: { id: milestoneId },
                    },
                  });
                }
              }

              await fetchMilestones({
                variables: {
                  where: { project: { id: currentProjectId } },
                  orderBy: 'dueDate_ASC',
                },
              });

              await refetchProject();

              let successMessage = `Successfully added ${tasksToConnect.length} task(s) to the project`;
              if (selectedMilestoneIds.length > 0) {
                successMessage += ` and ${selectedMilestoneIds.length} milestone(s)`;
              }
              if (convertedCount > 0) {
                successMessage += `. ${convertedCount} assignee(s) converted to collaborators`;
              }
              message.success(successMessage);
            } else {
              message.info('No new tasks to add');
            }
          } catch (error) {
            console.error('Failed to add tasks to project:', error);
            message.error('Failed to add tasks to the project. Please try again.');
            throw error;
          }
        }}
      />

      {/* Add to Milestone Modal */}
      <Modal
        title="Add Tasks to Milestone"
        open={isAddToMilestoneModalVisible}
        onCancel={() => {
          if (!isAddingToMilestone) {
            setIsAddToMilestoneModalVisible(false);
            setSelectedMilestoneId(null);
          }
        }}
        onOk={handleAddToMilestoneConfirm}
        okText="Add to Milestone"
        confirmLoading={isAddingToMilestone}
        okButtonProps={{ disabled: !selectedMilestoneId || isAddingToMilestone }}
        cancelButtonProps={{ disabled: isAddingToMilestone }}
        closable={!isAddingToMilestone}
        maskClosable={!isAddingToMilestone}
        width={600}
      >
        <div>
          <Text style={{ display: 'block', marginBottom: 16, color: '#64748b' }}>
            Select a milestone to add the {selectedTaskIds.length} selected task{selectedTaskIds.length > 1 ? 's' : ''} to:
          </Text>

          {/* Warning for tasks that will be reassigned */}
          {selectedMilestoneId && (() => {
            const tasksWithMilestones = selectedTaskIds.filter(taskId => {
              return milestones.some(m =>
                m.id !== selectedMilestoneId && m.tasks?.some(t => t.id === taskId)
              );
            });

            if (tasksWithMilestones.length > 0) {
              const affectedMilestones = new Set();
              tasksWithMilestones.forEach(taskId => {
                const m = milestones.find(m =>
                  m.id !== selectedMilestoneId && m.tasks?.some(t => t.id === taskId)
                );
                if (m) affectedMilestones.add(m.name);
              });

              return (
                <div
                  style={{
                    backgroundColor: '#fff7e6',
                    border: '1px solid #ffd591',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    marginBottom: 16,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'start',
                  }}
                >
                  <ExclamationCircleOutlined
                    style={{
                      color: '#fa8c16',
                      fontSize: '16px',
                      marginTop: 2,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ color: '#d46b08', display: 'block', marginBottom: 4 }}>
                      Warning: Task Reassignment
                    </Text>
                    <Text style={{ color: '#ad6800', fontSize: '13px' }}>
                      {tasksWithMilestones.length} of the selected task{tasksWithMilestones.length > 1 ? 's are' : ' is'} currently assigned to {affectedMilestones.size > 1 ? 'other milestones' : 'another milestone'} ({Array.from(affectedMilestones).join(', ')}). {tasksWithMilestones.length > 1 ? 'They' : 'It'} will be moved to the selected milestone.
                    </Text>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <Select
            placeholder="Select a milestone"
            style={{ width: '100%' }}
            value={selectedMilestoneId}
            onChange={setSelectedMilestoneId}
            size="large"
          >
            {(milestones || []).map((milestone) => (
              <Option key={milestone.id} value={milestone.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>{milestone.name}</Text>
                    {milestone.dueDate && (
                      <Text style={{ fontSize: '12px', color: '#64748b', marginLeft: 8 }}>
                        Due: {moment(milestone.dueDate).format('MMM D, YYYY')}
                      </Text>
                    )}
                  </div>
                  <Tag color={milestone.status === 'COMPLETED' ? 'success' : milestone.status === 'IN_PROGRESS' ? 'processing' : 'default'}>
                    {milestone.status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'N/A'}
                  </Tag>
                </div>
              </Option>
            ))}
          </Select>
          {(milestones || []).length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '24px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                marginTop: 16,
              }}
            >
              <Text style={{ color: '#94a3b8' }}>
                No milestones available. Please create a milestone first.
              </Text>
            </div>
          )}
        </div>
      </Modal>

      <PageContentLayout
        style={{
          backgroundColor: 'white',
          width: '100%',
          paddingTop: '2vh',
        }}
      >
        <Card
          style={{
            borderRadius: '7px',
            border: '1px solid #e5edf4',
            marginBottom: 50,
          }}
        >
          <Row gutter={[32, 16]}>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.completionPercentage}%
                </Text>
                <Text style={{ display: 'block', fontSize: '14px', color: '#64748b', marginTop: 4, marginBottom: 12 }}>
                  Completion
                </Text>
                <Progress
                  percent={stats.completionPercentage}
                  strokeColor="#3b82f6"
                  showInfo={false}
                />
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.completedTasks}
                </Text>
                <Text style={{ display: 'block', fontSize: '14px', color: '#64748b', marginTop: 4 }}>
                  Tasks Completed
                </Text>
                <Text style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginTop: 4 }}>
                  of {stats.totalTasks} total tasks
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: stats.daysLeft !== null ? (stats.daysLeft < 0 ? '#ef4444' : stats.daysLeft <= 7 ? '#faad14' : '#1f2937') : '#64748b',
                }}>
                  {stats.daysLeft !== null ? (stats.daysLeft >= 0 ? stats.daysLeft : 0) : 'N/A'}
                </Text>
                <Text style={{ display: 'block', fontSize: '14px', color: '#64748b', marginTop: 4 }}>
                  Day(s) Left
                </Text>
                {stats.daysLeft !== null && stats.daysLeft < 0 && (
                  <Text style={{ display: 'block', fontSize: '12px', color: '#ef4444', marginTop: 4 }}>
                    Overdue
                  </Text>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            className="custom-tabs-details-history"
            tabBarStyle={{
              border: '4px solid #f1f5f9',
              borderRadius: '8px',
              width: 'fit-content',
              display: 'inline-flex',
              gap: 0,
              margin: 0,
            }}
          >
            <TabPane tab="Tasks" key="1" />
            <TabPane tab="Milestones" key="2" />
            <TabPane tab="Gantt Chart" key="3" />
          </Tabs>

          {activeTab === '1' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input
                placeholder="Search tasks"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  width: 250,
                  height: '40px',
                  borderRadius: '5px',
                }}
                prefix={
                  <Button
                    type="text"
                    icon={<CloseOutlined style={{ fontSize: '13px' }} />}
                    onClick={() => setSearchText('')}
                  />
                }
              />
              <Button
                style={{
                  fontSize: '14px',
                  height: '40px',
                  borderRadius: '5px',
                }}
                onClick={() => setIsAddTaskModalVisible(true)}
              >
                Add Existing Task
              </Button>
              <Button
                type="primary"
                style={{
                  fontSize: '14px',
                  height: '40px',
                  borderRadius: '5px',
                  backgroundColor: '#3b82f6',
                }}
                onClick={() => {
                  history.push(`/tasks/form/new?projectId=${currentProjectId}`);
                }}
              >
                Create Task
              </Button>
            </div>
          )}
        </div>

        {activeTab === '1' && selectedTaskIds.length > 0 && (
          <div
            style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Text style={{ color: '#0369a1', fontWeight: 500 }}>
                {selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''} selected
                {(() => {
                  const totalTasks = searchText
                    ? (currentProject?.tasks || []).filter(task =>
                        task.name.toLowerCase().includes(searchText.toLowerCase())
                      ).length
                    : currentProject?.tasks?.length || 0;
                  return selectedTaskIds.length === totalTasks && totalTasks > 0
                    ? ' (all tasks)'
                    : '';
                })()}
              </Text>
              <Button
                size="small"
                type="text"
                onClick={() => setSelectedTaskIds([])}
                style={{ color: '#0369a1' }}
              >
                Clear selection
              </Button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddToMilestone}
                disabled={!milestones || milestones.length === 0 || isAddingToMilestone}
                loading={isAddingToMilestone}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Add to Milestone
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkRemoveTasks}
                disabled={isAddingToMilestone}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                Remove from Project
              </Button>
            </div>
          </div>
        )}

        {activeTab === '1' && (
          <TasksTable
            tasks={
              searchText
                ? (currentProject?.tasks || []).filter((task) =>
                    task.name.toLowerCase().includes(searchText.toLowerCase())
                  )
                : currentProject?.tasks || []
            }
          />
        )}
        {activeTab === '2' && (
          <ProjectMilestone currentProjectId={currentProjectId} />
        )}
        {activeTab === '3' && (
          <ProjectGantt currentProjectId={currentProjectId} />
        )}
      </PageContentLayout>
    </div>
  );
};

export default withRouter(ProjectDetails);
